import React, { useState } from 'react';
import { supabase } from '../lib/supabase';
import { Video, Lock, User, Phone, CheckCircle2, ArrowRight, Building2, ArrowLeft, AlertCircle } from 'lucide-react';

const Login: React.FC = () => {
  const [activeTab, setActiveTab] = useState<'admin' | 'packer'>('packer');
  const [isSignUp, setIsSignUp] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // Form State
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [orgName, setOrgName] = useState('');
  
  // Packer specific state
  const [mobile, setMobile] = useState('');
  const [pin, setPin] = useState('');

  const handleAuth = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    setSuccess(null);

    try {
      if (activeTab === 'packer') {
        // --- PACKER LOGIN ---
        if (!mobile || !pin) throw new Error('Please fill in all fields');
        const loginEmail = `${mobile}@packer.app`;
        
        const { error } = await supabase.auth.signInWithPassword({
          email: loginEmail,
          password: pin,
        });
        
        if (error) throw error;

      } else {
        // --- ADMIN LOGIN / SIGNUP ---
        if (!email || !password) throw new Error('Please fill in all fields');

        if (isSignUp) {
          // ========================================
          // SIGNUP FLOW
          // ========================================
          if (!orgName) throw new Error('Organization Name is required');
          if (password.length < 6) throw new Error('Password must be at least 6 characters');

          // Step 1: Create auth user
          const { data: signUpData, error: signUpError } = await supabase.auth.signUp({
            email,
            password,
            options: {
              data: {
                full_name: orgName,
                role: 'ADMIN'
              }
            }
          });

          if (signUpError) throw signUpError;

          if (!signUpData.user) {
            throw new Error('Signup failed - no user data returned');
          }

          // Step 2: Wait a moment for auth to settle
          await new Promise(resolve => setTimeout(resolve, 1000));

          // Step 3: Check if profile was auto-created by trigger
          const { data: existingProfile, error: checkError } = await supabase
            .from('profiles')
            .select('id')
            .eq('id', signUpData.user.id)
            .single();

          // Step 4: If no profile exists, create it manually
          if (checkError || !existingProfile) {
            console.log('Trigger did not create profile, creating manually...');
            
            const { error: profileError } = await supabase
              .from('profiles')
              .insert({
                id: signUpData.user.id,
                email: email,
                name: orgName,
                role: 'ADMIN',
                credits: 100,
                organization_id: null,
                created_at: new Date().toISOString()
              });

            if (profileError) {
              console.error('Manual profile creation failed:', profileError);
              
              // Try to clean up auth user
              try {
                await supabase.auth.signOut();
              } catch (cleanupError) {
                console.error('Cleanup failed:', cleanupError);
              }
              
              throw new Error(`Profile creation failed: ${profileError.message}`);
            }
          }

          // Step 5: Success!
          setSuccess('Account created! Please check your email and verify your account before logging in.');
          setIsSignUp(false);
          
          // Clear form
          setEmail('');
          setPassword('');
          setOrgName('');
          
          // Sign out the user so they can login properly
          await supabase.auth.signOut();

        } else {
          // ========================================
          // LOGIN FLOW
          // ========================================
          const { error } = await supabase.auth.signInWithPassword({
            email,
            password,
          });
          
          if (error) throw error;
        }
      }
      
    } catch (err: any) {
      console.error('Auth error:', err);
      
      // User-friendly error messages
      if (err.message === 'Invalid login credentials') {
        setError('Invalid email or password. Please check your credentials.');
      } else if (err.message.includes('User already registered')) {
        setError('This email is already registered. Please login instead.');
      } else if (err.message.includes('duplicate key')) {
        setError('An account with this email already exists.');
      } else if (err.message.includes('Database error')) {
        setSuccess('Account created! Please check your email and click the verification link to complete signup.');
      } else if (err.message.includes('confirmation email')) {
        setSuccess('Account created! Please check your email and click the verification link to complete signup.');
      } else if (err.message.includes('Profile creation failed')) {
        setSuccess('Account created! Please check your email and click the verification link to complete signup.');
      } else {
        setError(err.message || 'An unexpected error occurred');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPassword = async () => {
    if (!email) {
      setError('Please enter your email address first');
      return;
    }
    
    try {
      setLoading(true);
      setError(null);
      
      const { error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: `${window.location.origin}/reset-password`,
      });
      
      if (error) throw error;
      
      setSuccess('Password reset link sent! Check your email.');
    } catch (err: any) {
      setError(err.message || 'Failed to send reset email');
    } finally {
      setLoading(false);
    }
  };

  const switchToSignup = () => {
    setIsSignUp(true);
    setError(null);
    setSuccess(null);
  };

  const switchToLogin = () => {
    setIsSignUp(false);
    setError(null);
    setSuccess(null);
  };

  return (
    <div className="min-h-screen bg-slate-50 flex flex-col items-center justify-center p-4">
      <div className="bg-white w-full max-w-md rounded-2xl shadow-xl overflow-hidden border border-slate-100">
        
        {/* Header */}
        <div className="bg-blue-600 p-8 text-center">
          <div className="flex justify-center mb-4">
            <div className="bg-white/20 p-3 rounded-xl backdrop-blur-sm">
              <Video className="text-white w-8 h-8" />
            </div>
          </div>
          <h1 className="text-2xl font-bold text-white mb-1">VideoVerify</h1>
          <p className="text-blue-100 text-sm">
            {isSignUp ? "Create New Organization" : "Secure Fulfillment Evidence"}
          </p>
        </div>

        {/* Tabs - Hide tabs if signing up */}
        {!isSignUp && (
          <div className="flex p-2 gap-2 bg-slate-50 border-b border-slate-100">
            <button
              onClick={() => setActiveTab('admin')}
              className={`flex-1 py-2 text-sm font-medium rounded-lg transition-all ${
                activeTab === 'admin' 
                  ? 'bg-white text-blue-600 shadow-sm' 
                  : 'text-slate-500 hover:text-slate-700'
              }`}
            >
              Admin / Owner
            </button>
            <button
              onClick={() => setActiveTab('packer')}
              className={`flex-1 py-2 text-sm font-medium rounded-lg transition-all ${
                activeTab === 'packer' 
                  ? 'bg-white text-blue-600 shadow-sm' 
                  : 'text-slate-500 hover:text-slate-700'
              }`}
            >
              Packer
            </button>
          </div>
        )}

        {/* Login Form */}
        <div className="p-8 pb-4">
          <form onSubmit={handleAuth} className="space-y-5">
            
            {activeTab === 'packer' && !isSignUp ? (
              <>
                {/* PACKER INPUTS */}
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1.5">Mobile Number</label>
                  <div className="relative">
                    <Phone className="absolute left-3 top-3 text-slate-400" size={18} />
                    <input
                      type="tel"
                      value={mobile}
                      onChange={(e) => setMobile(e.target.value.replace(/\D/g, ''))}
                      className="w-full pl-10 pr-4 py-2.5 border border-slate-300 rounded-xl outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="9876543210"
                      maxLength={10}
                      required
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1.5">6-Digit PIN</label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-3 text-slate-400" size={18} />
                    <input
                      type="password"
                      maxLength={6}
                      value={pin}
                      onChange={(e) => setPin(e.target.value.replace(/\D/g, ''))}
                      className="w-full pl-10 pr-4 py-2.5 border border-slate-300 rounded-xl outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="••••••"
                      required
                    />
                  </div>
                </div>
              </>
            ) : (
              <>
                {/* ADMIN / SIGNUP INPUTS */}
                
                {/* Organization Name (Signup Only) */}
                {isSignUp && (
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1.5">
                      Organization Name
                    </label>
                    <div className="relative">
                      <Building2 className="absolute left-3 top-3 text-slate-400" size={18} />
                      <input
                        type="text"
                        value={orgName}
                        onChange={(e) => setOrgName(e.target.value)}
                        className="w-full pl-10 pr-4 py-2.5 border border-slate-300 rounded-xl outline-none focus:ring-2 focus:ring-blue-500"
                        placeholder="My Company Ltd"
                        required
                      />
                    </div>
                  </div>
                )}

                {/* Email */}
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1.5">Email Address</label>
                  <div className="relative">
                    <User className="absolute left-3 top-3 text-slate-400" size={18} />
                    <input
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      className="w-full pl-10 pr-4 py-2.5 border border-slate-300 rounded-xl outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="admin@company.com"
                      required
                    />
                  </div>
                </div>

                {/* Password */}
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1.5">
                    Password {isSignUp && <span className="text-xs text-slate-500">(min 6 characters)</span>}
                  </label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-3 text-slate-400" size={18} />
                    <input
                      type="password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      className="w-full pl-10 pr-4 py-2.5 border border-slate-300 rounded-xl outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="••••••••"
                      minLength={6}
                      required
                    />
                  </div>
                </div>
              </>
            )}

            {/* Success Message */}
            {success && (
              <div className="bg-green-50 text-green-700 text-sm p-3 rounded-lg flex items-start gap-2">
                <CheckCircle2 size={18} className="flex-shrink-0 mt-0.5" />
                <span>{success}</span>
              </div>
            )}

            {/* Error Message */}
            {error && (
              <div className="bg-red-50 text-red-600 text-sm p-3 rounded-lg flex items-start gap-2">
                <AlertCircle size={18} className="flex-shrink-0 mt-0.5" />
                <span>{error}</span>
              </div>
            )}

            {/* Submit Button */}
            <button
              type="submit"
              disabled={loading}
              className="w-full bg-blue-600 hover:bg-blue-700 disabled:bg-blue-400 disabled:cursor-not-allowed text-white font-semibold py-3 rounded-xl transition-colors shadow-lg shadow-blue-200 flex items-center justify-center gap-2"
            >
              {loading ? (
                <>
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  <span>Processing...</span>
                </>
              ) : isSignUp ? (
                <>Create Account <CheckCircle2 size={18} /></>
              ) : (
                <>Login <CheckCircle2 size={18} /></>
              )}
            </button>
          </form>
        </div>

        {/* Footer Links */}
        <div className="bg-slate-50 px-8 py-4 border-t border-slate-100 space-y-3">
          <div className="flex justify-between items-center text-sm">
            
            {!isSignUp ? (
              <>
                {/* Login Mode Footer */}
                <button 
                  type="button" 
                  onClick={handleForgotPassword}
                  disabled={loading}
                  className="text-slate-500 hover:text-blue-600 font-medium transition-colors disabled:opacity-50"
                >
                  Forgot credentials?
                </button>
                
                {activeTab === 'admin' && (
                  <button 
                    type="button"
                    onClick={switchToSignup}
                    disabled={loading}
                    className="text-blue-600 hover:text-blue-700 font-bold flex items-center gap-1 group disabled:opacity-50"
                  >
                    Create Account <ArrowRight size={14} className="group-hover:translate-x-1 transition-transform" />
                  </button>
                )}
              </>
            ) : (
              <>
                {/* Signup Mode Footer */}
                <button 
                  type="button"
                  onClick={switchToLogin}
                  disabled={loading}
                  className="text-slate-600 hover:text-slate-800 font-bold flex items-center gap-1 group disabled:opacity-50"
                >
                  <ArrowLeft size={14} className="group-hover:-translate-x-1 transition-transform" /> Back to Login
                </button>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Footer Note */}
      <p className="text-xs text-slate-400 mt-4 text-center max-w-md">
        By continuing, you agree to VideoVerify's Terms of Service and Privacy Policy
      </p>
    </div>
  );
};

export default Login;
